import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { patternsResources } from '@eis/patterns';
import de from './de.json' with { type: 'json' };
import en from './en.json' with { type: 'json' };

/**
 * UI-chrome i18n (Addendum B.2).
 *
 * Bundled, not HTTP-loaded: locale JSON is imported through Vite and code-split with the code
 * that uses it, so keys and screens ship atomically. The legacy `/public/locales` + cache-busting
 * arrangement existed to allow post-deploy edits; that need belongs to the *domain content* layer
 * (translation-management), not to UI chrome.
 *
 * Dates and numbers are deliberately NOT i18next's job — they go through the core civil-date and
 * `Intl` wrappers.
 */
export const shellResources = { de, en } as const;

export interface I18nSetup {
  readonly supportedLanguages: readonly string[];
  readonly defaultLanguage: string;
  /** Module namespaces, contributed by manifests. */
  readonly moduleResources?: readonly {
    readonly namespace: string;
    readonly resources: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  }[];
}

export async function createI18n(setup: I18nSetup): Promise<i18n> {
  const instance = i18next.createInstance();

  await instance.use(initReactI18next).init({
    lng: resolveInitialLanguage(setup),
    fallbackLng: 'en',
    supportedLngs: [...setup.supportedLanguages],
    defaultNS: 'shell',
    ns: ['shell', 'patterns'],
    interpolation: { escapeValue: false },
    resources: {
      de: { shell: de, patterns: patternsResources.de },
      en: { shell: en, patterns: patternsResources.en },
    },
  });

  for (const module of setup.moduleResources ?? []) {
    for (const [language, bundle] of Object.entries(module.resources)) {
      instance.addResourceBundle(language, module.namespace, bundle, true, true);
    }
  }

  document.documentElement.setAttribute('lang', instance.language);
  instance.on('languageChanged', (language: string) => {
    document.documentElement.setAttribute('lang', language);
  });

  return instance;
}

/**
 * Preference order: the user's own choice (the DE/EN switch, stored) → the environment's default
 * (`config.json` `i18n.defaultLanguage`, `de`). The browser's language is NOT consulted (owner,
 * 2026-10-05, SDD-022 DV-17): the console is German unless the user switches, whatever the
 * machine's locale — an English browser opened it in English, the demo notice included.
 */
export function resolveInitialLanguage(setup: I18nSetup): string {
  const stored = safeRead('eis.language');
  if (stored !== null && setup.supportedLanguages.includes(stored)) return stored;
  return setup.defaultLanguage;
}

export function persistLanguage(language: string): void {
  try {
    globalThis.localStorage?.setItem('eis.language', language);
  } catch {
    /* storage unavailable — the in-memory choice still applies for this session */
  }
}

function safeRead(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
